import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { employeeApi } from '../../services/employeeService';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone } from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatMoney, formatMonthYear, formatNumber, formatPercent } from '../../utils/format';

interface Employee {
  id: string; firstName: string; lastName: string;
  employeeProfile?: { position?: string; employmentType?: string } | null;
  locationAssignments?: { salon: { id: string; name: string } }[];
  salon?: { name: string };
}
interface DataSummary {
  clientsCount: number; servicesCount: number; servicesRevenue: number; productsRevenue: number;
  materialsCost: number; workedHours: number; avgVisitValue: number; revenuePerHour: number; productSharePct: number; totalRevenue: number; source: string;
}

const SOURCE: Record<string, { label: string; tone: PillTone }> = {
  AUTO: { label: 'Dane z wizyt i sprzedaży w systemie', tone: 'success' },
  MANUAL: { label: 'Dane wprowadzone ręcznie', tone: 'info' },
  MIXED: { label: 'Dane z systemu i wprowadzone ręcznie', tone: 'info' },
  IMPORT: { label: 'Dane z importu CSV', tone: 'info' },
  POS: { label: 'Dane z kasy (POS)', tone: 'success' },
  NONE: { label: 'Brak danych za ten miesiąc', tone: 'neutral' },
};

export const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const { activeSalonId } = useSalonContextStore();
  const [rows, setRows] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [detail, setDetail] = useState<{ emp: Employee; data: DataSummary } | null>(null);

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    setLoading(true);
    setLoadError(null);
    try { setRows(await employeeApi.list(activeSalonId)); }
    catch (e) {
      setRows([]);
      setLoadError((e as { response?: { status?: number } }).response?.status === 403
        ? 'Twoje konto nie ma dostępu do listy pracowników. Poproś właściciela salonu o nadanie uprawnień.'
        : 'Sprawdź połączenie i odśwież stronę.');
    }
    finally { setLoading(false); }
  }, [activeSalonId]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (emp: Employee) => {
    const [y, m] = month.split('-').map(Number);
    const data = await employeeApi.data(emp.id, y, m);
    setDetail({ emp, data });
  };

  // Deep-link ?staffId=... (np. klik w pracownika na dashboardzie) otwiera jego dane
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const sid = searchParams.get('staffId');
    if (!sid || rows.length === 0) return;
    const emp = rows.find((r) => r.id === sid);
    if (emp) openDetail(emp).catch(() => { /* ignore */ });
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line
  }, [rows]);

  const [y, m] = month.split('-').map(Number);
  const monthLabel = month ? formatMonthYear(new Date(y, m - 1, 1)) : '';

  const metrics = detail ? [
    { label: 'Klienci', value: formatNumber(detail.data.clientsCount) },
    { label: 'Wykonane usługi', value: formatNumber(detail.data.servicesCount) },
    { label: 'Godziny pracy', value: `${formatNumber(detail.data.workedHours)} h` },
    { label: 'Sprzedaż usług', value: formatMoney(detail.data.servicesRevenue) },
    { label: 'Sprzedaż produktów', value: formatMoney(detail.data.productsRevenue) },
    { label: 'Koszt materiałów', value: formatMoney(detail.data.materialsCost) },
    { label: 'Średnia wartość wizyty', value: formatMoney(detail.data.avgVisitValue) },
    { label: 'Obrót na godzinę', value: formatMoney(detail.data.revenuePerHour) },
    { label: 'Udział produktów', value: formatPercent(detail.data.productSharePct, Number.isInteger(detail.data.productSharePct) ? 0 : 1) },
  ] : [];
  const source = detail ? SOURCE[detail.data.source] || { label: 'Źródło danych nieznane', tone: 'neutral' as PillTone } : null;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Zespół' }, { label: 'Pracownicy', to: '/salon/staff' }]}
        title="Wyniki pracowników"
        description={!loading && rows.length
          ? `${countLabel(rows.length, 'osoba', 'osoby', 'osób')} · sprzedaż, klienci i godziny w wybranym miesiącu`
          : 'Sprzedaż, klienci i godziny pracy każdej osoby w wybranym miesiącu.'}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <TextField
            type="month"
            size="small"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            inputProps={{ 'aria-label': 'Miesiąc' }}
            sx={{ width: 190 }}
          />
        </Stack>
      </PageHeader>

      {loading ? (
        <LoadingState variant="table" rows={5} />
      ) : loadError ? (
        <SectionCard>
          <EmptyState icon={<LockOutlinedIcon />} title="Nie udało się wczytać pracowników" description={loadError} />
        </SectionCard>
      ) : rows.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<GroupOutlinedIcon />}
            title="Brak pracowników"
            description="Dodaj osoby do zespołu, aby zobaczyć ich wyniki."
            actions={<Button variant="contained" onClick={() => navigate('/salon/staff')}>Przejdź do pracowników</Button>}
          />
        </SectionCard>
      ) : (
        <SectionCard title="Zespół" subtitle={`Wyniki za ${monthLabel}`} padding="none">
          <TableContainer>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Imię i nazwisko</TableCell>
                  <TableCell>Stanowisko</TableCell>
                  <TableCell>Lokalizacje</TableCell>
                  <TableCell align="right" aria-label="Akcje" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={1.25}>
                        <PersonAvatar firstName={e.firstName} lastName={e.lastName} seed={e.id} size={32} />
                        <Typography sx={{ fontWeight: 600, fontSize: 'inherit', whiteSpace: 'nowrap' }}>{e.firstName} {e.lastName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: e.employeeProfile?.position ? tokens.ink : tokens.muted }}>{e.employeeProfile?.position || '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.75} flexWrap="wrap">
                        {(e.locationAssignments?.length ? e.locationAssignments.map((l) => l.salon.name) : [e.salon?.name]).filter(Boolean).map((n) => (
                          <StatusPill key={n} size="small" tone="neutral" noDot label={n} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" gap={1} justifyContent="flex-end">
                        {/* Profil zbiera wszystko o jednej osobie (uwaga klienta 4). */}
                        <Button size="small" variant="text" onClick={() => navigate(`/salon/staff/${e.id}`)}>Profil</Button>
                        <Button size="small" variant="outlined" onClick={() => openDetail(e)}>Wyniki</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {detail?.emp.firstName} {detail?.emp.lastName}
          <Typography component="div" sx={{ fontSize: '0.9375rem', color: tokens.muted, fontWeight: 500, mt: 0.25 }}>
            Wyniki za {monthLabel}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' } }}>
                {metrics.map((mt) => (
                  <Box key={mt.label} sx={{ bgcolor: tokens.surface, border: `1px solid ${tokens.line}`, borderRadius: '12px', px: 1.5, py: 1.25, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontWeight: 600 }}>{mt.label}</Typography>
                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 750, fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>{mt.value}</Typography>
                  </Box>
                ))}
              </Box>
              {source && (
                <Box sx={{ mt: 2 }}>
                  <StatusPill tone={source.tone} label={source.label} />
                </Box>
              )}
              {detail.data.source === 'NONE' && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Nie ma danych ze sprzedaży za ten miesiąc. Podłącz kasę (POS) albo wprowadź dane ręcznie lub importem CSV.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDetail(null)}>Zamknij</Button>
          {detail && (
            <Button variant="contained" onClick={() => { const id = detail.emp.id; setDetail(null); navigate(`/salon/staff/${id}`); }}>
              Otwórz profil
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesPage;
